/* eslint-disable no-param-reassign */
import { v4 as uuidv4 } from 'uuid';
import { splitIntoParagraphs } from '../../../utils/paragraphSplitter';
import IntentResponseStep from '../../../types/BotBuilder/IntentResponseStep';
import BotResponseTreeNode from '../../../types/BotBuilder/BotResponseTreeNode';
import ResponseCardsTreeNode from '../../../types/BotBuilder/ResponseCardsTreeNode';
import MessageBlockTreeNode from '../../../types/BotBuilder/MessageBlockTreeNode';
import IntentResponse from '../../../types/BotBuilder/IntentResponse';
import IntentTreeNode from '../../../types/BotBuilder/IntentTreeNode';
import Intent from '../../../types/BotBuilder/Intent';
import ResponseCard from '../../../types/ResponseCard';
import MessageBlockContent from '../../../types/BotBuilder/MessageBlockContent';
import validateNodeStructure from './nodeValidation';
import IntentStepType from '../../../types/BotBuilder/IntentStepType';
import GoToIntentTreeNode from '../../../types/BotBuilder/GoToIntentTreeNode';
import ResponseCardsTreeNodeItem from '../../../types/BotBuilder/ResponseCardsTreeNodeItem';
import SlotConditionalTreeNode from '../../../types/BotBuilder/SlotConditionalTreeNode';
import CollectVariableTreeNode from '../../../types/BotBuilder/CollectVariableTreeNode';
import IntentSlot from '../../../types/BotBuilder/IntentSlot';
import Offsets from '../../../types/BotBuilder/Offsets';
import Position from '../../../types/BotBuilder/Position';
import ConditionalTreeNode from '../../../types/BotBuilder/ConditionalTreeNode';
import GenerativeAiTreeNode from '../../../types/BotBuilder/GenerativeAiTreeNode';
import CalculationTreeNode from '../../../types/BotBuilder/CalculationTreeNode';
import SendEmailTreeNode from '../../../types/BotBuilder/SendEmailTreeNode';
import SendSmsTreeNode from '../../../types/BotBuilder/SendSmsTreeNode';
import DeleteVariableTreeNode from '../../../types/BotBuilder/DeleteVariableTreeNode';
import CodeBlockTreeNode from '../../../types/BotBuilder/CodeBlockTreeNode';
import LlmTreeNode from '../../../types/BotBuilder/LlmTreeNode';
import DoNothingTreeNode from '../../../types/BotBuilder/DoNothingTreeNode';
import SlotValueType from '../../../types/BotBuilder/SlotValueType';
import InlineEnumeratedSlotValue from '../../../types/BotBuilder/InlineEnumeratedSlotValue';

export interface TreeWalkerVisitor<TResult, TContext> {
  visit(node: BotResponseTreeNode, context: TContext): void;
  getResult(): TResult;
}

function pushIfDefined(
  children: BotResponseTreeNode[],
  ...steps: (BotResponseTreeNode | null | undefined)[]
): void {
  steps.forEach((step) => {
    if (step) children.push(step);
  });
}

export function getChildNodes(
  node: BotResponseTreeNode
): BotResponseTreeNode[] {
  const children: BotResponseTreeNode[] = [];
  const { content } = node;

  if (
    content instanceof IntentTreeNode ||
    content instanceof MessageBlockTreeNode ||
    content instanceof GenerativeAiTreeNode ||
    content instanceof CalculationTreeNode ||
    content instanceof SendEmailTreeNode ||
    content instanceof SendSmsTreeNode ||
    content instanceof DeleteVariableTreeNode ||
    content instanceof LlmTreeNode
  ) {
    pushIfDefined(children, content.nextStep);
  } else if (content instanceof CodeBlockTreeNode) {
    pushIfDefined(children, content.nextStep, content.nextStepOnError);
  } else if (content instanceof ResponseCardsTreeNode) {
    content.items.forEach((item) => pushIfDefined(children, item.nextStep));
  } else if (content instanceof CollectVariableTreeNode) {
    pushIfDefined(children, content.fallbackStep, content.slotProvidedStep);
    content.slotConditionals.forEach((c) =>
      pushIfDefined(children, c.nextStep)
    );
  } else if (content instanceof ConditionalTreeNode) {
    pushIfDefined(children, content.trueStep, content.falseStep);
  }

  return children;
}

export function walkTree<TResult, TContext>(
  startNode: BotResponseTreeNode,
  visitor: TreeWalkerVisitor<TResult, TContext>,
  context: TContext
): TResult {
  const nodesToCheck: BotResponseTreeNode[] = [startNode];
  const visitedNodeIds = new Set<string>();

  while (nodesToCheck.length > 0) {
    const currentNode = nodesToCheck.shift()!;

    if (!visitedNodeIds.has(currentNode.id)) {
      visitedNodeIds.add(currentNode.id);
      visitor.visit(currentNode, context);

      const children = getChildNodes(currentNode);
      nodesToCheck.push(...children);
    }
  }

  return visitor.getResult();
}

function createPositionTrackingVisitor(): TreeWalkerVisitor<
  { x: number; y: number },
  void
> {
  let maxX = -Infinity;
  let maxY = -Infinity;

  return {
    visit(node: BotResponseTreeNode): void {
      maxX = Math.max(maxX, node.xPosition || 0);
      maxY = Math.max(maxY, node.yPosition || 0);
    },
    getResult(): { x: number; y: number } {
      return { x: maxX, y: maxY };
    }
  };
}

function createReachableNodesVisitor(): TreeWalkerVisitor<Set<string>, void> {
  const reachable = new Set<string>();

  return {
    visit(node: BotResponseTreeNode): void {
      reachable.add(node.id);
    },
    getResult(): Set<string> {
      return reachable;
    }
  };
}

function createInlineSlotDataVisitor(): TreeWalkerVisitor<
  Map<
    string,
    {
      inlineValueType: SlotValueType;
      inlineEnumeratedValues?: InlineEnumeratedSlotValue[];
    }
  >,
  void
> {
  const inlineSlotData = new Map<
    string,
    {
      inlineValueType: SlotValueType;
      inlineEnumeratedValues?: InlineEnumeratedSlotValue[];
    }
  >();

  return {
    visit(node: BotResponseTreeNode): void {
      const c = node.content;

      if (c instanceof CollectVariableTreeNode) {
        if (c.slotType === null && c.slotValueType && c.slotName) {
          const inlineData: {
            inlineValueType: SlotValueType;
            inlineEnumeratedValues?: InlineEnumeratedSlotValue[];
          } = {
            inlineValueType: c.slotValueType
          };

          if (c.slotValueType === SlotValueType.ENUMERATED_VALUE) {
            inlineData.inlineEnumeratedValues =
              c.inlineEnumeratedValues ||
              c.slotConditionals
                .map((cond) => cond.slotValue)
                .filter(Boolean)
                .map((value) => ({ value, synonyms: [] }));
          }

          inlineSlotData.set(c.slotName, inlineData);
        }
      }

      if (c instanceof LlmTreeNode && c.outputVariableName) {
        inlineSlotData.set(c.outputVariableName, {
          inlineValueType: c.outputValueType ?? SlotValueType.FREE_TEXT
        });
      }
    },
    getResult(): Map<
      string,
      {
        inlineValueType: SlotValueType;
        inlineEnumeratedValues?: InlineEnumeratedSlotValue[];
      }
    > {
      return inlineSlotData;
    }
  };
}

export const findRightMostBottomMostPosition = (
  intentNode: Intent
): { x: number; y: number } => {
  const visitor = createPositionTrackingVisitor();
  return walkTree(intentNode.rootNode, visitor, undefined);
};

export function getPositionContentItems(node: BotResponseTreeNode): string[] {
  if (node.content instanceof MessageBlockTreeNode) {
    return node.content.messageBlocks.map((block) => block.content || '');
  }

  if (
    node.content instanceof CollectVariableTreeNode &&
    node.content.slotConditionals
  ) {
    return node.content.slotConditionals.map(
      (conditional) => conditional.slotValue ?? ''
    );
  }

  if (node.content instanceof ResponseCardsTreeNode && node.content.items) {
    return node.content.items.map((item) => item.text ?? '');
  }

  return [];
}

export function getFinalChildIndex(
  contentItems: string[],
  providedIndex?: number
): number {
  const defaultIndex = contentItems.length > 0 ? contentItems.length - 1 : 0;
  return providedIndex ?? defaultIndex;
}

export function getOffsetsForNode(
  node: BotResponseTreeNode,
  childType?:
    | 'goToIntent'
    | 'collectVariable'
    | 'slotConditional'
    | 'fallback'
    | 'provided'
    | 'conditional'
): Offsets {
  let xOffset = 100;
  let baseYOffset = 200;
  let yIncrementPerBlock = 40;
  let charMultiplier = 0.5;

  if (node.content instanceof MessageBlockTreeNode) {
    xOffset = 150;
    baseYOffset = 220;
    yIncrementPerBlock = 60;
    charMultiplier = 0.52;
  } else if (node.content instanceof ResponseCardsTreeNode) {
    xOffset = 500;
    baseYOffset = -300;
    yIncrementPerBlock = 200;

    if (childType === 'collectVariable') {
      xOffset = 1000;
      baseYOffset = -190;
    }
  } else if (node.content instanceof CollectVariableTreeNode) {
    xOffset = 150;
    baseYOffset = 220;
    yIncrementPerBlock = 60;
    charMultiplier = 0.52;

    if (childType === 'slotConditional') {
      xOffset = 500;
      baseYOffset = -300;
      yIncrementPerBlock = 200;
    }

    if (childType === 'collectVariable') {
      xOffset = 1000;
      baseYOffset = -190;
    }

    if (childType === 'fallback') {
      xOffset = -500;
      baseYOffset = 200;
    }
    if (childType === 'provided') {
      xOffset = 0;
      baseYOffset = 400;
    }
  }

  return {
    xOffset,
    baseYOffset,
    yIncrementPerBlock,
    charMultiplier
  };
}

export function calculateNewNodePosition(
  parentPos: Position,
  itemIndex: number,
  contentItems: string[],
  offsets: Offsets
): Position {
  const { xOffset, baseYOffset, yIncrementPerBlock, charMultiplier } = offsets;

  const x = parentPos.x + xOffset;

  let y = parentPos.y + baseYOffset;

  const totalChars = contentItems.reduce((acc, text) => acc + text.length, 0);

  y += totalChars * charMultiplier;

  y += itemIndex * yIncrementPerBlock;

  return { x, y };
}

// TODO this method is still used by response cards coming back from the API
// Real solution is the turn response cards into intent steps but that will take a larger reworking
export const calculateYPositionBasedOnContentLength = (
  messageBlocks: MessageBlockContent[],
  parentPositionY: number
): number => {
  //  Starting position of 120px below parent
  let y = parentPositionY + 120;
  //  Calculate the total number of characters in all the message blocks.
  const contentLength = messageBlocks
    .map((block: MessageBlockContent) => (block.content ?? '').length)
    .reduce((a: number, b: number) => a + b, 0);

  const charOffset = contentLength / 2;

  // Add 60px for every 500 characters in a response.
  const increments = Math.floor(contentLength / 500);
  y += increments * 70;

  // Add 40px for every message block in a response.
  y += messageBlocks.length * 40;

  return y + charOffset;
};

// Intent steps from the backend use a numbering system where each step has a unique number and
// they reference the next step to go to. This doesn't work for us because we want to display
// a complex tree of steps, and potentially add new steps, remove steps or add something in between.
// This takes a given step (referenced by its number) and converts it into a tree node.
export const intentStepsToTreeNode = (
  intentStepsByNumber: Record<number, IntentResponseStep>,
  intentStepNumber: number,
  intentSlots: IntentSlot[],
  cache: Record<string | number, BotResponseTreeNode> = {}
): BotResponseTreeNode => {
  function createNodes(): void {
    const stepNumbers = Object.keys(intentStepsByNumber).map((key) =>
      parseInt(key, 10)
    );
    stepNumbers.forEach((stepNumber) => {
      const step = intentStepsByNumber[stepNumber];
      if (cache[stepNumber]) return;
      let node: BotResponseTreeNode;
      switch (step.stepType) {
        case IntentStepType.ELICIT_SLOT: {
          const intentSlot = intentSlots.find(
            (slot) => slot.slotName === step.elicitSlotStep!.slotName
          );
          const globalSlotType = intentSlot?.slotType ?? null;

          node = {
            id: step.nodeId,
            content: new CollectVariableTreeNode(
              null,
              step.elicitSlotStep!.slotName,
              globalSlotType,
              step.elicitSlotStep!.slotType,
              null, // slotProvidedStep - linked in second pass
              null, // fallbackStep - linked in second pass
              step.elicitSlotStep!.enableInputFields,
              step.elicitSlotStep!.maxFileSize,
              step.elicitSlotStep!.allowedFileTypes,
              intentSlot?.inlineEnumeratedValues,
              step.elicitSlotStep!.elicitSlotCollectAsArray,
              step.elicitSlotStep!.allowFutureDates,
              step.elicitSlotStep!.autofillEnabled,
              [] // slotConditionals - linked in second pass
            ),
            xPosition: step.xPosition,
            yPosition: step.yPosition
          };
          break;
        }
        case IntentStepType.MESSAGE_BLOCK: {
          const messageBlockNode = new MessageBlockTreeNode(
            step.messageBlockStep!.content.map((block, blockOrder) => ({
              content: block.content,
              contentType: 'MARKDOWN' as const,
              blockOrder,
              id: `messageBlockContent-${stepNumber}-${blockOrder}`
            })),
            null, // nextStep - linked in second pass
            step.messageBlockStep!.category,
            step.messageBlockStep!.responseCardsXPosition,
            step.messageBlockStep!.responseCardsYPosition
          );
          node = {
            id: step.nodeId,
            content: messageBlockNode,
            xPosition: step.xPosition,
            yPosition: step.yPosition
          };
          if (step.messageBlockStep!.responseCards?.length) {
            const responseCardsNode = {
              id: uuidv4(),
              content: new ResponseCardsTreeNode([]),
              xPosition:
                step.messageBlockStep!.responseCardsXPosition ?? step.xPosition,
              yPosition:
                step.messageBlockStep!.responseCardsYPosition ??
                calculateYPositionBasedOnContentLength(
                  step.messageBlockStep!.content,
                  step.yPosition
                )
            };
            cache[`responseCards-${stepNumber}`] = responseCardsNode;
          }
          break;
        }
        case IntentStepType.GENERATIVE_AI:
          node = {
            id: step.nodeId,
            content: new GenerativeAiTreeNode(null),
            xPosition: step.xPosition,
            yPosition: step.yPosition
          };
          break;
        case IntentStepType.DO_NOTHING:
          node = {
            id: step.nodeId,
            content: new DoNothingTreeNode(),
            xPosition: step.xPosition,
            yPosition: step.yPosition
          };
          break;
        case IntentStepType.GO_TO_INTENT:
          node = {
            id: step.nodeId,
            content: new GoToIntentTreeNode(
              step.goToIntentStep!.intentId
                ? {
                    id: step.goToIntentStep!.intentId,
                    name: step.goToIntentStep!.intentName || '',
                    slots: step.goToIntentStep!.intentSlots || []
                  }
                : null,
              step.goToIntentStep!.resumeStep,
              step.goToIntentStep!.slotMappings || []
            ),
            xPosition: step.xPosition,
            yPosition: step.yPosition
          };
          break;
        case IntentStepType.CONDITIONAL: {
          const { variableName, comparisonOperator, comparisonValue } =
            step.conditionalStep!;
          const conditionalNode = new ConditionalTreeNode(
            null,
            variableName,
            null,
            comparisonOperator,
            comparisonValue,
            null, // trueStep - linked later
            null // falseStep - linked later
          );
          node = {
            id: step.nodeId,
            content: conditionalNode,
            xPosition: step.xPosition,
            yPosition: step.yPosition
          };
          break;
        }
        case IntentStepType.SEND_EMAIL: {
          const sendEmailNode = new SendEmailTreeNode(
            step.sendEmailStep!.recipient,
            step.sendEmailStep!.subject,
            step.sendEmailStep!.content,
            step.sendEmailStep!.attachmentVariableName ?? null,
            null,
            step.sendEmailStep!.slotDisplayConditions ?? []
          );
          node = {
            id: step.nodeId,
            content: sendEmailNode,
            xPosition: step.xPosition,
            yPosition: step.yPosition
          };
          break;
        }
        case IntentStepType.SEND_SMS: {
          const sendSmsNode = new SendSmsTreeNode(
            step.sendSmsStep!.recipient,
            step.sendSmsStep!.content,
            null,
            step.sendSmsStep!.slotDisplayConditions ?? []
          );
          node = {
            id: step.nodeId,
            content: sendSmsNode,
            xPosition: step.xPosition,
            yPosition: step.yPosition
          };
          break;
        }
        case IntentStepType.DELETE_VARIABLE: {
          const deleteVariableNode = new DeleteVariableTreeNode(
            step.deleteVariableStep!.variableName,
            null
          );
          node = {
            id: step.nodeId,
            content: deleteVariableNode,
            xPosition: step.xPosition,
            yPosition: step.yPosition
          };
          break;
        }
        case IntentStepType.CODE_BLOCK: {
          const codeBlockNode = new CodeBlockTreeNode(
            step.codeBlockStep!.code,
            step.codeBlockStep!.environmentVariables,
            step.codeBlockStep!.outputMappings,
            null
          );
          node = {
            id: step.nodeId,
            content: codeBlockNode,
            xPosition: step.xPosition,
            yPosition: step.yPosition
          };
          break;
        }
        case IntentStepType.LLM: {
          const llmNode = new LlmTreeNode(
            step.llmStep!.prompt,
            step.llmStep!.outputVariableName,
            step.llmStep!.aiModel,
            step.llmStep!.temperature,
            null,
            step.llmStep!.errorMessage,
            step.llmStep!.outputValueType ?? SlotValueType.FREE_TEXT
          );
          node = {
            id: step.nodeId,
            content: llmNode,
            xPosition: step.xPosition,
            yPosition: step.yPosition
          };
          break;
        }
        case IntentStepType.ARITHMETIC:
        case IntentStepType.DATE: {
          const calculationNode = new CalculationTreeNode(
            null, // nextStep - linked later
            step.arithmeticStep || null,
            step.dateStep || null
          );
          node = {
            id: step.nodeId,
            content: calculationNode,
            xPosition: step.xPosition,
            yPosition: step.yPosition
          };
          break;
        }
        default:
          throw new Error(`Unsupported step type: ${step.stepType}`);
      }
      cache[stepNumber] = node;
    });
  }

  function connectNodes(): void {
    const stepNumbers = Object.keys(intentStepsByNumber).map((key) =>
      parseInt(key, 10)
    );
    stepNumbers.forEach((stepNumber) => {
      const step = intentStepsByNumber[stepNumber];
      const node = cache[stepNumber];
      if (step.stepType === IntentStepType.ELICIT_SLOT) {
        const elicitSlotNode = node.content as CollectVariableTreeNode;
        const elicitSlotData = step.elicitSlotStep!;
        const { slotConditionals } = elicitSlotData;
        if (elicitSlotData.fallbackStep !== null) {
          if (elicitSlotData.fallbackStep === stepNumber) {
            elicitSlotNode.fallbackStep = node;
          } else if (cache[elicitSlotData.fallbackStep]) {
            elicitSlotNode.fallbackStep = cache[elicitSlotData.fallbackStep];
          }
        }
        if (elicitSlotData.slotProvidedStep !== null) {
          if (elicitSlotData.slotProvidedStep === stepNumber) {
            // self-reference; do nothing
          } else if (cache[elicitSlotData.slotProvidedStep]) {
            elicitSlotNode.slotProvidedStep =
              cache[elicitSlotData.slotProvidedStep];
          }
        }
        elicitSlotNode.slotConditionals = slotConditionals.map((cond) => {
          let nextStep = null;
          if (cond.nextStep !== null) {
            if (cond.nextStep === stepNumber) {
              nextStep = node;
            } else if (cache[cond.nextStep]) {
              nextStep = cache[cond.nextStep];
            }
          }
          return new SlotConditionalTreeNode(cond.slotValue, nextStep);
        });
      } else if (step.stepType === IntentStepType.MESSAGE_BLOCK) {
        const messageBlockNode = node.content as MessageBlockTreeNode;
        const messageBlockData = step.messageBlockStep!;
        if (messageBlockData.responseCards?.length) {
          const responseCardsKey = `responseCards-${stepNumber}`;
          const responseCardsNode = cache[responseCardsKey];
          const responseCardsContent =
            responseCardsNode.content as ResponseCardsTreeNode;
          responseCardsContent.items = messageBlockData.responseCards.map(
            (card) => {
              let nextStep = null;
              if (card.nextStep !== null) {
                if (card.nextStep === stepNumber) {
                  nextStep = node;
                } else if (cache[card.nextStep]) {
                  nextStep = cache[card.nextStep];
                }
              }
              return new ResponseCardsTreeNodeItem(
                card.text,
                nextStep,
                card.nextStep
              );
            }
          );
          messageBlockNode.nextStep = responseCardsNode;
        } else if (
          messageBlockData.nextStep !== null &&
          messageBlockData.nextStep !== undefined
        ) {
          if (messageBlockData.nextStep === stepNumber) {
            messageBlockNode.nextStep = node;
          } else if (cache[messageBlockData.nextStep]) {
            messageBlockNode.nextStep = cache[messageBlockData.nextStep];
          }
        }
      } else if (step.stepType === IntentStepType.CONDITIONAL) {
        const conditionalNode = node.content as ConditionalTreeNode;
        if (step.conditionalStep) {
          if (cache[step.conditionalStep.nextStepIfTrue] !== undefined) {
            conditionalNode.trueStep =
              cache[step.conditionalStep.nextStepIfTrue];
          }
          if (cache[step.conditionalStep.nextStepIfFalse] !== undefined) {
            conditionalNode.falseStep =
              cache[step.conditionalStep.nextStepIfFalse];
          }
        }
      } else if (step.stepType === IntentStepType.GENERATIVE_AI) {
        const generativeAiNode = node.content as GenerativeAiTreeNode;
        const generativeAiData = step.generativeAiStep!;
        if (
          generativeAiData.nextStep !== null &&
          generativeAiData.nextStep !== undefined
        ) {
          if (generativeAiData.nextStep === stepNumber) {
            generativeAiNode.nextStep = node;
          } else if (cache[generativeAiData.nextStep]) {
            generativeAiNode.nextStep = cache[generativeAiData.nextStep];
          }
        }
      } else if (step.stepType === IntentStepType.SEND_EMAIL) {
        const sendEmailNode = node.content as SendEmailTreeNode;
        const nextStepNumber = step.sendEmailStep?.nextStep;
        if (nextStepNumber !== null && nextStepNumber !== undefined) {
          if (nextStepNumber === stepNumber) {
            sendEmailNode.nextStep = node;
          } else if (cache[nextStepNumber]) {
            sendEmailNode.nextStep = cache[nextStepNumber];
          }
        }
      } else if (step.stepType === IntentStepType.SEND_SMS) {
        const sendSmsNode = node.content as SendSmsTreeNode;
        const nextStepNumber = step.sendSmsStep?.nextStep;
        if (nextStepNumber !== null && nextStepNumber !== undefined) {
          if (nextStepNumber === stepNumber) {
            sendSmsNode.nextStep = node;
          } else if (cache[nextStepNumber]) {
            sendSmsNode.nextStep = cache[nextStepNumber];
          }
        }
      } else if (step.stepType === IntentStepType.DELETE_VARIABLE) {
        const deleteVariableNode = node.content as DeleteVariableTreeNode;
        const nextStepNumber = step.deleteVariableStep?.nextStep;
        if (nextStepNumber !== null && nextStepNumber !== undefined) {
          if (nextStepNumber === stepNumber) {
            deleteVariableNode.nextStep = node;
          } else if (cache[nextStepNumber]) {
            deleteVariableNode.nextStep = cache[nextStepNumber];
          }
        }
      } else if (step.stepType === IntentStepType.CODE_BLOCK) {
        const codeBlockNode = node.content as CodeBlockTreeNode;
        const nextStepNumber = step.codeBlockStep?.nextStep;
        if (nextStepNumber !== null && nextStepNumber !== undefined) {
          if (nextStepNumber === stepNumber) {
            codeBlockNode.nextStep = node;
          } else if (cache[nextStepNumber]) {
            codeBlockNode.nextStep = cache[nextStepNumber];
          }
        }
        const errorStepNumber = step.codeBlockStep?.nextStepOnError;
        if (errorStepNumber !== null && errorStepNumber !== undefined) {
          if (errorStepNumber === stepNumber) {
            codeBlockNode.nextStepOnError = node;
          } else if (cache[errorStepNumber]) {
            codeBlockNode.nextStepOnError = cache[errorStepNumber];
          }
        }
      } else if (step.stepType === IntentStepType.LLM) {
        const llmNode = node.content as LlmTreeNode;
        const nextStepNumber = step.llmStep?.nextStep;
        if (nextStepNumber !== null && nextStepNumber !== undefined) {
          if (nextStepNumber === stepNumber) {
            llmNode.nextStep = node;
          } else if (cache[nextStepNumber]) {
            llmNode.nextStep = cache[nextStepNumber];
          }
        }
      } else if (
        step.stepType === IntentStepType.ARITHMETIC ||
        step.stepType === IntentStepType.DATE
      ) {
        const calculationNode = node.content as CalculationTreeNode;
        const nextStepNumber =
          step.arithmeticStep?.nextStep || step.dateStep?.nextStep;
        if (nextStepNumber !== null && nextStepNumber !== undefined) {
          if (nextStepNumber === stepNumber) {
            calculationNode.nextStep = node;
          } else if (cache[nextStepNumber]) {
            calculationNode.nextStep = cache[nextStepNumber];
          }
        }
      }
    });
  }

  createNodes();
  connectNodes();

  return cache[intentStepNumber];
};

function collectInlineSlotData(nodes: BotResponseTreeNode[]): Map<
  string,
  {
    inlineValueType: SlotValueType;
    inlineEnumeratedValues?: InlineEnumeratedSlotValue[];
  }
> {
  const visitor = createInlineSlotDataVisitor();
  nodes.forEach((node) => walkTree(node, visitor, undefined));
  return visitor.getResult();
}

type IntentResponseWithCategory = IntentResponse & { category: string };

export const apiToDomain = (
  intent: IntentResponse | IntentResponseWithCategory
): { intent: Intent; unconnectedNodes: BotResponseTreeNode[] } => {
  const intentStepsByStepNumber: Record<number, IntentResponseStep> =
    intent.responseSteps.reduce(
      (acc, step) => {
        acc[step.stepNumber] = step;
        return acc;
      },
      {} as Record<number, IntentResponseStep>
    );

  const firstStep: IntentResponseStep = intentStepsByStepNumber[0];

  const cache: Record<number | string, BotResponseTreeNode> = {};

  const rootChildNode = intentStepsToTreeNode(
    intentStepsByStepNumber,
    0,
    intent.slots,
    cache
  );

  const responseTree: BotResponseTreeNode = {
    id: 'root',
    content: new IntentTreeNode(intent.sampleUtterances, rootChildNode),
    xPosition: firstStep.xPosition - 180,
    yPosition: firstStep.yPosition - 180
  };

  const visitor = createReachableNodesVisitor();
  const reachable = walkTree(responseTree, visitor, undefined);

  const unconnectedNodes = Object.values(cache).filter(
    (node) => !reachable.has(node.id)
  );

  const inlineSlotData = collectInlineSlotData([
    responseTree,
    ...unconnectedNodes
  ]);
  const existingSlotNames = new Set(intent.slots.map((slot) => slot.slotName));
  const updatedSlots = intent.slots.map((slot) => {
    const inlineData = inlineSlotData.get(slot.slotName);

    if (!inlineData) {
      return slot;
    }

    return {
      ...slot,
      inlineValueType: inlineData.inlineValueType,
      inlineEnumeratedValues: inlineData.inlineEnumeratedValues
    };
  });
  const newInlineSlots = Array.from(inlineSlotData.entries())
    .filter(([slotName]) => !existingSlotNames.has(slotName))
    .map(([slotName, inlineData]) => ({
      id: null,
      slotName,
      slotType: '',
      inlineValueType: inlineData.inlineValueType,
      inlineEnumeratedValues: inlineData.inlineEnumeratedValues
    }));

  const domainIntent: Intent = {
    id: intent.id || null,
    name: intent.name,
    description: intent.description,
    safeguarding: intent.safeguarding,
    safeguardingRecipient: intent.safeguardingRecipient,
    locked: intent.locked ?? false,
    category: intent.category || null,
    slots: [...updatedSlots, ...newInlineSlots],
    rootNode: responseTree
  };

  return { intent: domainIntent, unconnectedNodes };
};

const extractResponseCards = (
  node: BotResponseTreeNode | null
): ResponseCard[] => {
  if (!node || !(node.content instanceof ResponseCardsTreeNode)) {
    return [];
  }

  return node.content.items.map((item) => ({
    text: item.text,
    value: item.text,
    nextStep: item.stepNumber || null
  }));
};

export function splitContentIntoMessageBlocks(
  content: string | null
): MessageBlockContent[] {
  if (!content || content.trim().length === 0) {
    return [
      {
        content: null,
        contentType: 'MARKDOWN',
        blockOrder: 0,
        id: uuidv4()
      }
    ];
  }

  const cleanedContent = content.replace(/<br\s*\/?>/gi, '\n');
  const normalisedContent = cleanedContent.replace(/\\\n/g, '\n');
  const splitResult = splitIntoParagraphs(normalisedContent);

  if (!splitResult.shouldSplit) {
    return [
      {
        content: normalisedContent,
        contentType: 'MARKDOWN',
        blockOrder: 0,
        id: uuidv4()
      }
    ];
  }

  return splitResult.paragraphs.map((paragraph, index) => ({
    content: paragraph ?? null,
    contentType: 'MARKDOWN',
    blockOrder: index,
    id: uuidv4()
  }));
}

function botResponseNodeToResponseSteps(
  node: BotResponseTreeNode,
  counter: { value: number },
  nodeIdToStep: Record<string, number> = {}
): IntentResponseStep[] {
  if (node.content instanceof IntentTreeNode) {
    return node.content.nextStep
      ? botResponseNodeToResponseSteps(
          node.content.nextStep,
          counter,
          nodeIdToStep
        )
      : [];
  }

  if (nodeIdToStep[node.id] !== undefined) {
    return [];
  }

  const responseSteps: IntentResponseStep[] = [];
  const currentStepNumber = counter.value;
  counter.value += 1;
  nodeIdToStep[node.id] = currentStepNumber;

  if (node.content instanceof CollectVariableTreeNode) {
    responseSteps.push({
      stepNumber: currentStepNumber,
      stepType: IntentStepType.ELICIT_SLOT,
      xPosition: node.xPosition,
      yPosition: node.yPosition,
      nodeId: node.id,
      elicitSlotStep: {
        slotName: node.content.slotName,
        slotType: node.content.slotValueType,
        slotProvidedStep: null,
        fallbackStep: null,
        slotConditionals: [],
        enableInputFields: node.content.enableInputFields,
        maxFileSize: node.content.maxFileSize,
        allowedFileTypes: node.content.allowedFileTypes,
        elicitSlotCollectAsArray: node.content.collectAsArray,
        allowFutureDates: node.content.allowFutureDates,
        autofillEnabled: node.content.autofillEnabled
      }
    });
    const baseStepIndex = responseSteps.length - 1;

    if (node.content.fallbackStep) {
      const fbNode = node.content.fallbackStep;
      if (nodeIdToStep[fbNode.id] !== undefined) {
        responseSteps[baseStepIndex].elicitSlotStep!.fallbackStep =
          nodeIdToStep[fbNode.id];
      } else {
        const fallbackSteps = botResponseNodeToResponseSteps(
          fbNode,
          counter,
          nodeIdToStep
        );
        if (fallbackSteps.length > 0) {
          responseSteps[baseStepIndex].elicitSlotStep!.fallbackStep =
            fallbackSteps[0].stepNumber;
          responseSteps.push(...fallbackSteps);
        }
      }
    }

    if (node.content.slotProvidedStep) {
      const provNode = node.content.slotProvidedStep;
      if (nodeIdToStep[provNode.id] !== undefined) {
        responseSteps[baseStepIndex].elicitSlotStep!.slotProvidedStep =
          nodeIdToStep[provNode.id];
      } else {
        const providedSteps = botResponseNodeToResponseSteps(
          provNode,
          counter,
          nodeIdToStep
        );
        if (providedSteps.length > 0) {
          responseSteps[baseStepIndex].elicitSlotStep!.slotProvidedStep =
            providedSteps[0].stepNumber;
          responseSteps.push(...providedSteps);
        }
      }
    }

    node.content.slotConditionals.forEach((cond) => {
      let nextStepNumber: number | null = null;
      if (cond.nextStep) {
        const condNode = cond.nextStep;
        if (nodeIdToStep[condNode.id] !== undefined) {
          nextStepNumber = nodeIdToStep[condNode.id];
        } else {
          const condSteps = botResponseNodeToResponseSteps(
            condNode,
            counter,
            nodeIdToStep
          );
          if (condSteps.length > 0) {
            nextStepNumber = condSteps[0].stepNumber;
            responseSteps.push(...condSteps);
          }
        }
      }
      responseSteps[baseStepIndex].elicitSlotStep!.slotConditionals.push({
        slotValue: cond.slotValue,
        nextStep: nextStepNumber
      });
    });

    return responseSteps;
  }

  if (node.content instanceof MessageBlockTreeNode) {
    const processedMessageBlocks = node.content.messageBlocks.flatMap((block) =>
      splitContentIntoMessageBlocks(block.content)
    );

    if (
      node.content.nextStep &&
      node.content.nextStep.content instanceof ResponseCardsTreeNode
    ) {
      const responseCardsNode = node.content.nextStep.content;
      const responseCards: ResponseCard[] = responseCardsNode.items.map(
        (item) => {
          let nextStepNumber = null;
          if (item.nextStep) {
            const childNode = item.nextStep;
            if (nodeIdToStep[childNode.id] !== undefined) {
              nextStepNumber = nodeIdToStep[childNode.id];
            } else {
              const childSteps = botResponseNodeToResponseSteps(
                childNode,
                counter,
                nodeIdToStep
              );
              nextStepNumber =
                childSteps.length > 0 ? childSteps[0].stepNumber : null;
              if (childSteps.length > 0) {
                responseSteps.push(...childSteps);
              }
            }
          }
          return {
            text: item.text,
            value: item.text,
            nextStep: nextStepNumber
          };
        }
      );
      responseSteps.unshift({
        stepNumber: currentStepNumber,
        stepType: IntentStepType.MESSAGE_BLOCK,
        xPosition: node.xPosition,
        yPosition: node.yPosition,
        nodeId: node.id,
        messageBlockStep: {
          category: node.content.category!,
          content: processedMessageBlocks,
          responseCards,
          nextStep: undefined,
          responseCardsXPosition: node.content.nextStep?.xPosition,
          responseCardsYPosition: node.content.nextStep?.yPosition
        }
      });
      return responseSteps;
    }
    const responseCards: ResponseCard[] = extractResponseCards(
      node.content.nextStep
    );
    responseSteps.push({
      stepNumber: currentStepNumber,
      stepType: IntentStepType.MESSAGE_BLOCK,
      xPosition: node.xPosition,
      yPosition: node.yPosition,
      nodeId: node.id,
      messageBlockStep: {
        category: node.content.category!,
        content: processedMessageBlocks,
        responseCards,
        nextStep: undefined,
        responseCardsXPosition: node.content.nextStep?.xPosition,
        responseCardsYPosition: node.content.nextStep?.yPosition
      }
    });
    if (node.content.nextStep) {
      const childNode = node.content.nextStep;
      if (nodeIdToStep[childNode.id] !== undefined) {
        responseSteps[responseSteps.length - 1].messageBlockStep!.nextStep =
          nodeIdToStep[childNode.id];
      } else {
        const childSteps = botResponseNodeToResponseSteps(
          childNode,
          counter,
          nodeIdToStep
        );
        if (childSteps.length > 0) {
          responseSteps[responseSteps.length - 1].messageBlockStep!.nextStep =
            childSteps[0].stepNumber;
          responseSteps.push(...childSteps);
        }
      }
    }
    return responseSteps;
  }

  if (node.content instanceof ConditionalTreeNode) {
    const trueSteps = botResponseNodeToResponseSteps(
      node.content.trueStep!,
      counter,
      nodeIdToStep
    );
    const falseSteps = botResponseNodeToResponseSteps(
      node.content.falseStep!,
      counter,
      nodeIdToStep
    );

    const trueStepNumber =
      trueSteps.length > 0
        ? trueSteps[0].stepNumber
        : nodeIdToStep[node.content.trueStep!.id];
    const falseStepNumber =
      falseSteps.length > 0
        ? falseSteps[0].stepNumber
        : nodeIdToStep[node.content.falseStep!.id];

    responseSteps.push({
      stepNumber: currentStepNumber,
      stepType: IntentStepType.CONDITIONAL,
      xPosition: node.xPosition,
      yPosition: node.yPosition,
      nodeId: node.id,
      conditionalStep: {
        variableName: node.content.slotName!,
        comparisonOperator: node.content.comparisonOperator!,
        comparisonValue: node.content.comparisonValue,
        nextStepIfTrue: trueStepNumber,
        nextStepIfFalse: falseStepNumber
      }
    });
    responseSteps.push(...trueSteps, ...falseSteps);
    return responseSteps;
  }

  if (node.content instanceof GoToIntentTreeNode) {
    responseSteps.push({
      stepNumber: currentStepNumber,
      stepType: IntentStepType.GO_TO_INTENT,
      xPosition: node.xPosition,
      yPosition: node.yPosition,
      nodeId: node.id,
      goToIntentStep: {
        intentId: node.content.intentId!,
        resumeStep: node.content.resumeStep!,
        slotMappings: node.content.slotMappings || []
      }
    });
    return responseSteps;
  }

  if (node.content instanceof DoNothingTreeNode) {
    responseSteps.push({
      stepNumber: currentStepNumber,
      stepType: IntentStepType.DO_NOTHING,
      xPosition: node.xPosition,
      yPosition: node.yPosition,
      nodeId: node.id
    });
    return responseSteps;
  }

  if (node.content instanceof GenerativeAiTreeNode) {
    responseSteps.push({
      stepNumber: currentStepNumber,
      stepType: IntentStepType.GENERATIVE_AI,
      xPosition: node.xPosition,
      yPosition: node.yPosition,
      nodeId: node.id,
      generativeAiStep: {
        nextStep: undefined
      }
    });

    if (node.content.nextStep) {
      const childNode = node.content.nextStep;
      if (nodeIdToStep[childNode.id] !== undefined) {
        responseSteps[responseSteps.length - 1].generativeAiStep!.nextStep =
          nodeIdToStep[childNode.id];
      } else {
        const childSteps = botResponseNodeToResponseSteps(
          childNode,
          counter,
          nodeIdToStep
        );
        if (childSteps.length > 0) {
          responseSteps[responseSteps.length - 1].generativeAiStep!.nextStep =
            childSteps[0].stepNumber;
          responseSteps.push(...childSteps);
        }
      }
    }

    return responseSteps;
  }

  if (node.content instanceof SendEmailTreeNode) {
    let nextStepNumber: number | null = null;

    const sendEmailStep = {
      stepNumber: currentStepNumber,
      stepType: IntentStepType.SEND_EMAIL,
      xPosition: node.xPosition,
      yPosition: node.yPosition,
      nodeId: node.id,
      sendEmailStep: {
        recipient: node.content.recipient,
        subject: node.content.subject,
        content: node.content.content,
        attachmentVariableName: node.content.attachmentVariableName,
        nextStep: null as number | null,
        slotDisplayConditions: node.content.slotDisplayConditions
      }
    };

    responseSteps.push(sendEmailStep);

    if (node.content.nextStep) {
      const childNode = node.content.nextStep;
      if (nodeIdToStep[childNode.id] !== undefined) {
        nextStepNumber = nodeIdToStep[childNode.id];
      } else {
        const childSteps = botResponseNodeToResponseSteps(
          childNode,
          counter,
          nodeIdToStep
        );
        if (childSteps.length > 0) {
          nextStepNumber = nodeIdToStep[childNode.id];
          responseSteps.push(...childSteps);
        }
      }

      sendEmailStep.sendEmailStep.nextStep = nextStepNumber;
    }

    return responseSteps;
  }

  if (node.content instanceof SendSmsTreeNode) {
    let nextStepNumber: number | null = null;

    const sendSmsStep = {
      stepNumber: currentStepNumber,
      stepType: IntentStepType.SEND_SMS,
      xPosition: node.xPosition,
      yPosition: node.yPosition,
      nodeId: node.id,
      sendSmsStep: {
        recipient: node.content.phoneNumber,
        content: node.content.content,
        nextStep: null as number | null,
        slotDisplayConditions: node.content.slotDisplayConditions
      }
    };

    responseSteps.push(sendSmsStep);

    if (node.content.nextStep) {
      const childNode = node.content.nextStep;
      if (nodeIdToStep[childNode.id] !== undefined) {
        nextStepNumber = nodeIdToStep[childNode.id];
      } else {
        const childSteps = botResponseNodeToResponseSteps(
          childNode,
          counter,
          nodeIdToStep
        );
        if (childSteps.length > 0) {
          nextStepNumber = nodeIdToStep[childNode.id];
          responseSteps.push(...childSteps);
        }
      }

      sendSmsStep.sendSmsStep.nextStep = nextStepNumber;
    }

    return responseSteps;
  }

  if (node.content instanceof DeleteVariableTreeNode) {
    let nextStepNumber: number | null = null;

    const deleteVariableStep = {
      stepNumber: currentStepNumber,
      stepType: IntentStepType.DELETE_VARIABLE,
      xPosition: node.xPosition,
      yPosition: node.yPosition,
      nodeId: node.id,
      deleteVariableStep: {
        variableName: node.content.variableName,
        nextStep: null as number | null
      }
    };

    responseSteps.push(deleteVariableStep);

    if (node.content.nextStep) {
      const childNode = node.content.nextStep;
      if (nodeIdToStep[childNode.id] !== undefined) {
        nextStepNumber = nodeIdToStep[childNode.id];
      } else {
        const childSteps = botResponseNodeToResponseSteps(
          childNode,
          counter,
          nodeIdToStep
        );
        if (childSteps.length > 0) {
          nextStepNumber = nodeIdToStep[childNode.id];
          responseSteps.push(...childSteps);
        }
      }

      deleteVariableStep.deleteVariableStep.nextStep = nextStepNumber;
    }

    return responseSteps;
  }

  if (node.content instanceof CodeBlockTreeNode) {
    let nextStepNumber: number | null = null;
    let errorStepNumber: number | null = null;

    const codeBlockStep = {
      stepNumber: currentStepNumber,
      stepType: IntentStepType.CODE_BLOCK,
      xPosition: node.xPosition,
      yPosition: node.yPosition,
      nodeId: node.id,
      codeBlockStep: {
        code: node.content.code,
        environmentVariables: node.content.environmentVariables,
        outputMappings: node.content.outputMappings,
        nextStep: null as number | null,
        nextStepOnError: null as number | null
      }
    };

    responseSteps.push(codeBlockStep);

    if (node.content.nextStep) {
      const childNode = node.content.nextStep;
      if (nodeIdToStep[childNode.id] !== undefined) {
        nextStepNumber = nodeIdToStep[childNode.id];
      } else {
        const childSteps = botResponseNodeToResponseSteps(
          childNode,
          counter,
          nodeIdToStep
        );
        if (childSteps.length > 0) {
          nextStepNumber = nodeIdToStep[childNode.id];
          responseSteps.push(...childSteps);
        }
      }
      codeBlockStep.codeBlockStep.nextStep = nextStepNumber;
    }

    if (node.content.nextStepOnError) {
      const childNode = node.content.nextStepOnError;
      if (nodeIdToStep[childNode.id] !== undefined) {
        errorStepNumber = nodeIdToStep[childNode.id];
      } else {
        const childSteps = botResponseNodeToResponseSteps(
          childNode,
          counter,
          nodeIdToStep
        );
        if (childSteps.length > 0) {
          errorStepNumber = nodeIdToStep[childNode.id];
          responseSteps.push(...childSteps);
        }
      }
      codeBlockStep.codeBlockStep.nextStepOnError = errorStepNumber;
    }

    return responseSteps;
  }

  if (node.content instanceof LlmTreeNode) {
    let nextStepNumber: number | null = null;

    const llmStep = {
      stepNumber: currentStepNumber,
      stepType: IntentStepType.LLM,
      xPosition: node.xPosition,
      yPosition: node.yPosition,
      nodeId: node.id,
      llmStep: {
        prompt: node.content.prompt,
        outputVariableName: node.content.outputVariableName,
        aiModel: node.content.aiModel,
        temperature: node.content.temperature,
        nextStep: null as number | null,
        errorMessage: node.content.errorMessage,
        outputValueType: node.content.outputValueType
      }
    };

    responseSteps.push(llmStep);

    if (node.content.nextStep) {
      const childNode = node.content.nextStep;
      if (nodeIdToStep[childNode.id] !== undefined) {
        nextStepNumber = nodeIdToStep[childNode.id];
      } else {
        const childSteps = botResponseNodeToResponseSteps(
          childNode,
          counter,
          nodeIdToStep
        );
        if (childSteps.length > 0) {
          nextStepNumber = nodeIdToStep[childNode.id];
          responseSteps.push(...childSteps);
        }
      }

      llmStep.llmStep.nextStep = nextStepNumber;
    }

    return responseSteps;
  }

  if (node.content instanceof CalculationTreeNode) {
    const calculationContent = node.content;
    let nextStepNumber: number | null = null;

    if (calculationContent.nextStep) {
      const childNode = calculationContent.nextStep;
      if (nodeIdToStep[childNode.id] !== undefined) {
        nextStepNumber = nodeIdToStep[childNode.id];
      } else {
        const childSteps = botResponseNodeToResponseSteps(
          childNode,
          counter,
          nodeIdToStep
        );
        if (childSteps.length > 0) {
          nextStepNumber = childSteps[0].stepNumber;
          responseSteps.push(...childSteps);
        }
      }
    }

    if (calculationContent.arithmeticStep) {
      responseSteps.unshift({
        stepNumber: currentStepNumber,
        stepType: IntentStepType.ARITHMETIC,
        xPosition: node.xPosition,
        yPosition: node.yPosition,
        nodeId: node.id,
        arithmeticStep: {
          ...calculationContent.arithmeticStep,
          nextStep: nextStepNumber
        }
      });
    } else if (calculationContent.dateStep) {
      responseSteps.unshift({
        stepNumber: currentStepNumber,
        stepType: IntentStepType.DATE,
        xPosition: node.xPosition,
        yPosition: node.yPosition,
        nodeId: node.id,
        dateStep: {
          ...calculationContent.dateStep,
          nextStep: nextStepNumber
        }
      });
    }

    return responseSteps;
  }

  return responseSteps;
}
export const domainToApi = (
  intent: Intent,
  unconnectedNodes: BotResponseTreeNode[]
): IntentResponse => {
  const filteredUnconnectedNodes = unconnectedNodes.filter((node) => {
    if (node.content instanceof MessageBlockTreeNode) {
      const messageNode = node.content;
      const hasContent = messageNode.messageBlocks.some(
        (block) => block.content && block.content.trim() !== ''
      );
      return hasContent;
    }
    return true;
  });

  const validationErrors = validateNodeStructure(intent.rootNode, intent.id);
  if (validationErrors.length > 0) {
    throw new Error(JSON.stringify(validationErrors));
  }

  const rootNode = intent.rootNode.content as IntentTreeNode;
  const { sampleUtterances } = rootNode;
  const counter = { value: 0 };
  const nodeIdToStep: Record<string, number> = {};
  const connectedSteps = botResponseNodeToResponseSteps(
    intent.rootNode,
    counter,
    nodeIdToStep
  );
  const unconnectedSteps = filteredUnconnectedNodes.flatMap((node) => {
    if (nodeIdToStep[node.id] !== undefined) {
      return [];
    }
    return botResponseNodeToResponseSteps(node, counter, nodeIdToStep);
  });

  const responseSteps = [
    ...connectedSteps.map((step) => ({ ...step, isConnectedToRoot: true })),
    ...unconnectedSteps.map((step) => ({ ...step, isConnectedToRoot: false }))
  ];

  const allNodes = [intent.rootNode, ...filteredUnconnectedNodes];
  const inlineSlotData = collectInlineSlotData(allNodes);

  const updatedSlots = intent.slots.map((slot) => {
    const inlineData = inlineSlotData.get(slot.slotName);
    if (inlineData) {
      return {
        ...slot,
        slotType: slot.slotType ?? '',
        inlineValueType: inlineData.inlineValueType,
        inlineEnumeratedValues: inlineData.inlineEnumeratedValues
      };
    }
    return {
      ...slot,
      slotType: slot.slotType ?? '',
      inlineValueType: slot.inlineValueType,
      inlineEnumeratedValues: slot.inlineEnumeratedValues
    };
  });

  const existingSlotNames = new Set(intent.slots.map((slot) => slot.slotName));
  const newInlineSlots = Array.from(inlineSlotData.entries())
    .filter(([slotName]) => !existingSlotNames.has(slotName))
    .map(([slotName, inlineData]) => ({
      id: null,
      slotName,
      slotType: '',
      inlineValueType: inlineData.inlineValueType,
      inlineEnumeratedValues: inlineData.inlineEnumeratedValues
    }));

  const finalSlots = [...updatedSlots, ...newInlineSlots];

  return {
    id: intent.id,
    name: intent.name ?? 'Untitled question',
    sampleUtterances,
    slots: finalSlots,
    responseSteps,
    description: intent.description,
    safeguarding: intent.safeguarding,
    safeguardingRecipient: intent.safeguardingRecipient ?? null,
    locked: intent.locked ?? false,
    category: intent.category ?? null
  };
};

export const createEmptyIntent = (
  name?: string,
  sampleUtterances?: string[]
): Intent => {
  const mbContent: MessageBlockContent[] = [
    {
      content: null,
      contentType: 'MARKDOWN',
      blockOrder: 0,
      id: uuidv4()
    }
  ];

  const messageBlockNode: BotResponseTreeNode = {
    id: uuidv4(),
    content: new MessageBlockTreeNode(
      mbContent,
      null,
      null,
      undefined,
      undefined
    ),
    xPosition: 200,
    yPosition: 200
  };

  const rootNode: BotResponseTreeNode = {
    id: 'root',
    content: new IntentTreeNode(sampleUtterances || [], messageBlockNode),
    xPosition: 0,
    yPosition: 0
  };

  return {
    id: null,
    name: name ?? null,
    description: null,
    safeguarding: false,
    safeguardingRecipient: null,
    locked: false,
    category: null,
    slots: [],
    rootNode
  };
};

export const getSlotsByValueType = (
  intent: Intent | null,
  unconnectedNodes: BotResponseTreeNode[],
  valueType: SlotValueType
): { slotName: string; label: string }[] => {
  if (!intent) return [];

  const existingSlots = intent.slots
    .filter((slot) => slot.inlineValueType === valueType)
    .map((slot) => ({
      slotName: slot.slotName,
      label: slot.slotName
    }));

  const allNodes = [intent.rootNode, ...unconnectedNodes];
  const inlineSlotData = collectInlineSlotData(allNodes);

  const inlineSlots = Array.from(inlineSlotData.entries())
    .filter(([, inlineData]) => inlineData.inlineValueType === valueType)
    .map(([slotName]) => ({
      slotName,
      label: slotName
    }));

  const allSlots = [...existingSlots, ...inlineSlots];
  const uniqueSlots = new Map();
  allSlots.forEach((slot) => {
    uniqueSlots.set(slot.slotName, slot);
  });

  return Array.from(uniqueSlots.values());
};
